/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PublicPagesHub — directory of public table views for an application.
 * Route: /dashboard/applications/:id/public-pages
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import { usePublicIndexConfig, usePublicViewConfig } from '../hooks/usePublicViewConfig';
import PublicIndexConfigModal from '../components/public-view-config/PublicIndexConfigModal';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const PUBLIC_CLIENT = process.env.REACT_APP_PUBLIC_CLIENT_URL || '';

export default function PublicPagesHub() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id: appId } = useParams();
  const [showIndexModal, setShowIndexModal] = useState(false);
  const [page, setPage] = useState(1);

  const { data: indexConfig } = usePublicIndexConfig(appId);

  // Fetch application details (name + description for auto-fill)
  const { data: appDetails } = useQuery({
    queryKey: ['applicationDetails', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(`/arcee/application/details/${appId}/`, { method: 'GET', signal });
      if (!resp.ok) throw new Error('Failed to load application details');
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  // Fetch all tables for this application
  const { data: tablesData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['publicPagesTableList', appId, page],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page=${page}&page_size=50`,
        { method: 'GET', signal }
      );
      if (!resp.ok) {
        let body = {};
        try { body = await resp.json(); } catch (_) {}
        throw new Error(body.error || body.message || `Failed to load tables (${resp.status})`);
      }
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 0,
  });

  const tables = tablesData?.tables || tablesData?.results || [];
  const publicTables = tables.filter(t => t.visibility === 'public');
  const indexIsActive = indexConfig?.is_active !== false;
  const indexUrl = `${PUBLIC_CLIENT}/p/`;

  return (
    <div
      className="d-flex flex-column w-100"
      style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}
    >
      {/* Header */}
      <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm">
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-light rounded-circle shadow-sm"
            onClick={() => navigate(`/dashboard/applications/${appId}`)}
            style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-arrow-left" />
          </button>
          <div>
            <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="bi bi-globe2" style={{ color: theme.primary }} />
              Public Pages
            </h4>
            <small className="text-muted d-none d-md-block">
              Configure and publish table data as public-facing pages.
            </small>
          </div>
        </div>
        <a
          href={indexUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-secondary"
        >
          <i className="bi bi-box-arrow-up-right me-1" />
          Open /p/ Directory
        </a>
      </div>

      <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1200 }}>

        {/* Index Page card */}
        <div className="bg-white rounded border shadow-sm p-4 mb-4">
          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, background: theme.primary + '15', color: theme.primary, fontSize: 22 }}
              >
                <i className="bi bi-house" />
              </div>
              <div>
                <div className="fw-bold">Index Page</div>
                <div className="text-muted small">/p/ — directory of all active public tables</div>
                <div className="mt-1">
                  <span className={`badge ${indexIsActive ? 'bg-success' : 'bg-secondary'}`}>
                    {indexIsActive ? 'Active' : 'Inactive'}
                  </span>
                  {indexConfig?.page_title && (
                    <span className="ms-2 text-muted small">"{indexConfig.page_title}"</span>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowIndexModal(true)}
              >
                <i className="bi bi-gear me-1" />
                Configure
              </button>
              <a
                href={indexUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm"
                style={{ background: theme.primary, color: '#fff', border: 'none' }}
              >
                <i className="bi bi-box-arrow-up-right me-1" />
                Open
              </a>
            </div>
          </div>
        </div>

        {/* Public Tables */}
        <div className="mb-3 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0">
            Public Tables
            <span className="ms-2 badge bg-primary" style={{ fontSize: 13 }}>{publicTables.length}</span>
          </h5>
          {publicTables.length > 0 && (
            <small className="text-muted">{publicTables.length} of {tables.length} tables are public</small>
          )}
        </div>

        {isLoading && (
          <div className="text-center py-5">
            <span className="spinner-border" style={{ color: theme.primary }} />
          </div>
        )}

        {isError && (
          <div className="alert alert-danger">
            {error?.message || 'Failed to load tables.'}{' '}
            <button className="btn btn-sm btn-outline-danger ms-2" onClick={refetch}>Retry</button>
          </div>
        )}

        {!isLoading && !isError && publicTables.length === 0 && (
          <div className="bg-white rounded border shadow-sm p-5 text-center text-muted">
            <i className="bi bi-globe" style={{ fontSize: 48, opacity: 0.25 }} />
            <div className="fw-semibold mt-3">No public tables yet</div>
            <div className="small mt-1">
              Set a table's visibility to <strong>Public</strong> in the table settings, then configure it here.
            </div>
            <button
              className="btn btn-sm btn-outline-primary mt-3"
              onClick={() => navigate(`/dashboard/applications/${appId}`)}
            >
              Go to Tables
            </button>
          </div>
        )}

        {!isLoading && !isError && publicTables.length > 0 && (
          <div className="bg-white rounded border shadow-sm overflow-hidden">
            <table className="table table-hover mb-0">
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th className="small text-muted fw-semibold py-2 px-3">Table</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Slug</th>
                  <th className="small text-muted fw-semibold py-2 px-3">View Type</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Status</th>
                  <th className="small text-muted fw-semibold py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {publicTables.map(table => (
                  <PublicTableRow
                    key={table.id}
                    table={table}
                    appId={appId}
                    navigate={navigate}
                    theme={theme}
                    publicClientBase={PUBLIC_CLIENT}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showIndexModal && (
        <PublicIndexConfigModal
          appId={appId}
          appName={appDetails?.display_name || appDetails?.name || ''}
          appDescription={appDetails?.description || ''}
          onClose={() => setShowIndexModal(false)}
        />
      )}
    </div>
  );
}

const VIEW_TYPE_LABELS = {
  card: 'Card Grid',
  list: 'List',
  table: 'Table',
  timeline: 'Timeline',
};

function PublicTableRow({ table, appId, navigate, theme, publicClientBase }) {
  // Use String(table.id) so the cache key matches what PublicViewConfigPage invalidates
  const { data: config, isLoading: configLoading } = usePublicViewConfig(String(table.id));

  // Prefer the slug from the view config (always up-to-date after saves);
  // fall back to the table's raw slug/name while the config is still loading.
  const slug = config?.table_slug || table.slug || table.name;
  const publicUrl = `${publicClientBase}/p/${slug}`;

  const viewTypeLabel = config?.view_type_label || (config?.view_type_key ? VIEW_TYPE_LABELS[config.view_type_key] || config.view_type_key : null);

  const isConfigured = config !== null && config !== undefined;
  const isActive = config?.is_active === true;

  return (
    <tr>
      <td className="px-3 py-2 align-middle">
        <div className="fw-semibold small">{table.display_name || table.name}</div>
        <div className="text-muted" style={{ fontSize: 11 }}>{table.name}</div>
      </td>
      <td className="px-3 py-2 align-middle">
        <code className="small" style={{ color: theme.primary }}>
          /p/{slug}
        </code>
      </td>
      <td className="px-3 py-2 align-middle">
        {configLoading ? (
          <span className="spinner-border spinner-border-sm text-secondary" style={{ width: 12, height: 12 }} />
        ) : viewTypeLabel ? (
          <span className="badge bg-light text-dark border small">{viewTypeLabel}</span>
        ) : (
          <span className="text-muted small">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle">
        {configLoading ? (
          <span className="spinner-border spinner-border-sm text-secondary" style={{ width: 12, height: 12 }} />
        ) : !isConfigured ? (
          <span className="badge bg-light text-muted border">Not configured</span>
        ) : isActive ? (
          <span className="badge bg-success">Live</span>
        ) : (
          <span className="badge bg-warning text-dark">Inactive</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm"
            style={{ background: theme.primary, color: '#fff', border: 'none' }}
            onClick={() => navigate(`/dashboard/applications/${appId}/public-pages/${table.id}/configure`)}
          >
            <i className="bi bi-sliders me-1" />
            {isConfigured ? 'Edit' : 'Configure'}
          </button>
          {isConfigured && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-secondary"
            >
              <i className="bi bi-box-arrow-up-right" />
            </a>
          )}
        </div>
      </td>
    </tr>
  );
}
